import { useGetProjectFileLazy } from "hooks/projectFiles/useGetProjectFileLazy";
import useModalState from "hooks/useModalState";
import { useEffect, useMemo } from "react";

import { Icon, IconButton, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import Modal from "modules/Modal/Modal";
import ProjectFileExplorer from "./ProjectFileExplorer";

export default function ProjectFileExplorerInput({
  submitFn,
  folderId,
  showLabel = false,
  compact = false,
  align = "right",
  showCompactLabel = true,
  sx,
}: {
  submitFn: (folderId: string | string[]) => void;
  folderId: string;
  showLabel?: boolean;
  compact?: boolean;
  align?: "left" | "right";
  showCompactLabel?: boolean;
  sx?: React.CSSProperties;
}) {
  const [getProjectFile, { graphqlData: fileById, loading, missing }] = useGetProjectFileLazy();

  useEffect(() => {
    if (folderId) getProjectFile({ id: folderId });
  }, [folderId, getProjectFile]);

  const folderName = useMemo(() => {
    if (!folderId) return "—";
    if (loading) return "Loading…";
    if (missing) return "Folder unavailable";
    return fileById?.name || `Folder #${folderId}`;
  }, [folderId, fileById?.name, loading, missing]);

  const {
    open: projectFilesModalOpen,
    onClose: onCloseProjectFilesModal,
    onOpen: onOpenProjectFilesModal,
  } = useModalState();

  return (
    <>
      <MDBox sx={sx} py={1}>
        {showLabel && !compact && (
          <MDTypography variant="caption" color="text">
            Project Files
          </MDTypography>
        )}

        {compact ? (
          <MDBox
            display="flex"
            justifyContent={showCompactLabel ? "space-between" : align === "left" ? "flex-start" : "flex-end"}
            alignItems="center"
            gap={1}
          >
            {showCompactLabel && (
              <MDTypography variant="caption" color="text">
                Project Files
              </MDTypography>
            )}
            <MDBox display="flex" alignItems="center" justifyContent={align === "left" ? "flex-start" : "flex-end"} gap={1}>
              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ wordBreak: "break-word", textAlign: align }}
              >
                {folderName}
              </MDTypography>
              <Tooltip title="Edit Project Files">
                <IconButton size="small" onClick={onOpenProjectFilesModal}>
                  <Icon fontSize="small">edit</Icon>
                </IconButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ) : (
          <>
            <MDBox
              display="flex"
              justifyContent={align === "left" ? "flex-start" : "flex-end"}
              alignItems="center"
              gap={1.25}
            >
              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ textAlign: align, wordBreak: "break-word" }}
              >
                {folderName}
              </MDTypography>
              <Tooltip title="Edit Project Files">
                <IconButton size="small" onClick={onOpenProjectFilesModal}>
                  <Icon fontSize="small">edit</Icon>
                </IconButton>
              </Tooltip>
            </MDBox>
          </>
        )}
      </MDBox>

      <Modal open={projectFilesModalOpen} onClose={onCloseProjectFilesModal} styles={{ overflow: "hidden" }}>
        <MDBox px={3} pt={3}>
          <MDTypography variant="h6">Project Files</MDTypography>
        </MDBox>

        <MDBox px={3} pb={3} sx={{ height: "calc(100% - 56px)" }}>
          <ProjectFileExplorer
            initialSelectedItems={folderId || null}
            selectionMode="folder"
            mode="folder-select"
            showSubmitButton
            submitLabel="Use this folder"
            height="100%"
            submitFn={(lastSelectedItem) => {
              submitFn(lastSelectedItem);
              onCloseProjectFilesModal();
            }}
          />
        </MDBox>
      </Modal>
    </>
  );
}
